import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useContext,
} from "react";
import { Form } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import { DAY_WEEK_TYPE } from "Pages/MyScheduleBeta/utils/constants";
import {
  toISTStart,
  toISTEnd,
  fromISTStart,
  fromISTEnd,
} from "Pages/MyScheduleBeta/utils/dateHelpers";

import FilterHeader from "./FilterHeader";
import FilterFormBody from "./FilterFormBody";
import FilterFooter from "./FilterFooter";
import AutoSearchInput from "utilFunctions/shared/AutoSearchInput";
import { BASE_URL, GLOBAL_SEARCH } from "utilFunctions/Constants/urlConstants";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import useResponsive from "Context/useResponsive";
import { UserInfoContext } from "Context/CommonContext";

interface FiltersFormScreenProps {
  onClose?: () => void;
  initialValues?: any;
  isOpen?: boolean;
}

export interface FiltersFormScreenRef {
  handleReset: () => void;
}

const getDefaultDay = (): Dayjs => dayjs();
const getDefaultWeek = (): [Dayjs, Dayjs] => [
  dayjs().subtract(3, "day"),
  dayjs().add(3, "day"),
];

const FiltersFormScreen = forwardRef<
  FiltersFormScreenRef,
  FiltersFormScreenProps
>(({ onClose, initialValues, isOpen }, ref) => {
  const [form] = Form.useForm();
  const { isDesktop } = useResponsive();

  const userInfo = useContext<any>(UserInfoContext);
  const subjects: any = userInfo?.subjects || [];
  const {
    eventType,
    selectedDate,
    filterState,
    setFilterState,
    setSelectedDate,
    setEventType,
    setSearchedFaculty,
  } = useScheduleBetaContext();
  const [selectedSubject, setSelectedSubject] = useState<string>(
    filterState?.subject?.value || "ALL"
  );
  const [input, setInput] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [searchURL, setSearchURL] = useState<string>("");
  const [searchName, setSearchName] = useState<any>(null);
  const [selectedFaculty, setSelectedFaculty] = useState<any>(
    filterState?.facultySearch || null
  );
  const limit = 10;
  const previousIsOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      setSelectedSubject(filterState?.subject?.value || "ALL");
      const faculty = filterState?.facultySearch || null;
      setSelectedFaculty(faculty);
      if (faculty) {
        const facultyEmail =
          faculty?.email ||
          faculty?.facultyEmail ||
          faculty?.userEmail ||
          faculty?.username ||
          "";
        setInput(facultyEmail);
        setSearchName(facultyEmail);
      } else {
        setInput("");
        setSearchName(null);
      }
      if (eventType === DAY_WEEK_TYPE.DAY) {
        const selected = selectedDate ? dayjs(selectedDate) : getDefaultDay();
        setDateValue([selected, selected]);
      } else {
        const [start, end] = getFilterDateRange();
        setDateValue([start, end]);
      }
    }
    previousIsOpen.current = !!isOpen;
  }, [isOpen]);
  const { makeRequest: GetSearchData, responseData: searchData } = useGetApi(
    () => setIsLoader(false),
    (error: any) => {
      console.error("Search API Error:", error);
      setIsLoader(false);
      setHasMore(false);
    }
  );
  useEffect(() => {
    if (searchData && searchData["data"]) {
      if (offset === 0) {
        setSuggestions([...searchData["data"]]);
      } else {
        setSuggestions((prev) => [...prev, ...searchData["data"]]);
      }

      setHasMore(searchData["data"].length > 0);
    }
  }, [searchData, offset]);

  useEffect(() => {
    if (!filterState?.facultySearch) {
      setSelectedFaculty(null);
      setInput("");
      setSuggestions([]);
      setSearchName(null);
      setOffset(0);
      setSearchURL("");
      setHasMore(false);
    }
  }, [filterState?.facultySearch]);
  const searchDataHandler = (searchTerm: string) => {
    const url = `${
      BASE_URL + GLOBAL_SEARCH
    }?name=${searchTerm}&moduleType=faculty&fexRole=Faculty,Academic Head,Regional Head`;

    setSearchURL(url);
    setOffset(0);
    setIsLoader(true);

    GetSearchData(`${url}&limit=${limit}&offset=0`);
  };
  const nextHandler = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    GetSearchData(`${searchURL}&limit=${limit}&offset=${newOffset}`);
  };
  const onClickHandler = (faculty: any) => {
    setSelectedFaculty(faculty);

    const facultyEmail =
      faculty?.email ||
      faculty?.facultyEmail ||
      faculty?.userEmail ||
      faculty?.username ||
      "";

    setInput(facultyEmail);
    setSearchName(facultyEmail);
  };

  const handleFacultyInputChange = (value: string) => {
    setInput(value);
    setSelectedFaculty((previousFaculty: any) => {
      if (!previousFaculty) {
        return previousFaculty;
      }

      const selectedEmail =
        previousFaculty?.email ||
        previousFaculty?.facultyEmail ||
        previousFaculty?.userEmail ||
        previousFaculty?.username ||
        "";

      return value === selectedEmail ? previousFaculty : null;
    });
  };

  const getFilterDateRange = (): [Dayjs, Dayjs] => {
    if (filterState?.startDate && filterState?.endDate) {
      const startDate = fromISTStart(filterState.startDate);
      const endDate = fromISTEnd(filterState.endDate);
      if (startDate && endDate) {
        return [dayjs(startDate), dayjs(endDate)];
      }
    }
    return getDefaultWeek();
  };

  const [dateValue, setDateValue] = useState<Dayjs | [Dayjs, Dayjs] | null>(
    () => {
      if (eventType === DAY_WEEK_TYPE.DAY) {
        const selected = selectedDate ? dayjs(selectedDate) : getDefaultDay();
        return [selected, selected];
      }
      return getFilterDateRange();
    }
  );

  const [resetKey, setResetKey] = useState<number>(0);
  useEffect(() => {
    if (!isOpen) {
      if (eventType === DAY_WEEK_TYPE.DAY) {
        const selected = selectedDate ? dayjs(selectedDate) : getDefaultDay();
        setDateValue([selected, selected]);
      } else {
        const [start, end] = getFilterDateRange();
        setDateValue([start, end]);
      }
    }
  }, [
    isOpen,
    eventType,
    selectedDate,
    filterState?.startDate,
    filterState?.endDate,
  ]);
  const handleReset = () => {
    form.resetFields();
    const today = getDefaultDay();
    let startDate: Dayjs;
    let endDate: Dayjs;
    if (eventType === DAY_WEEK_TYPE.DAY) {
      startDate = today;
      endDate = today;
      setDateValue([today, today]);
      setEventType?.(DAY_WEEK_TYPE.DAY);
      setSelectedDate?.(today.format("YYYY-MM-DD"));
    } else {
      startDate = today.subtract(3, "day");
      endDate = today.add(3, "day");

      setDateValue([startDate, endDate]);
      setEventType?.(DAY_WEEK_TYPE.WEEK);
      setSelectedDate?.(startDate.format("YYYY-MM-DD"));
    }

    // Reset local states
    setSelectedSubject("ALL");
    setSelectedFaculty(null);
    setInput("");
    setSuggestions([]);
    setSearchName(null);
    setOffset(0);
    setSearchURL("");
    setHasMore(false);
    setResetKey((prev) => prev + 1);
    setSearchedFaculty(null);

    setFilterState((prev: any) => ({
      ...prev,
      startDate: toISTStart(startDate.toDate()),
      endDate: toISTEnd(endDate.toDate()),
      subject: null,
      facultySearch: "",
      clearFacultySearchInput: isDesktop,
    }));

    localStorage.removeItem("scheduledFilter");
  };

  useImperativeHandle(ref, () => ({
    handleReset,
  }));

  const handleFinish = () => {
    let startDateVal: string | null = null;
    let endDateVal: string | null = null;

    if (dateValue) {
      if (Array.isArray(dateValue)) {
        const [start, end] = dateValue;

        if (start && end) {
          const isDifferentDay = !start.isSame(end, "day");

          if (isDifferentDay) {
            setEventType?.(DAY_WEEK_TYPE.WEEK);
            setSelectedDate?.(start.format("YYYY-MM-DD"));
          } else {
            setEventType?.(DAY_WEEK_TYPE.DAY);
            setSelectedDate?.(start.format("YYYY-MM-DD"));
          }
          startDateVal = toISTStart(start.toDate());
          endDateVal = toISTEnd(end.toDate());
        }
      } else {
        const picked = dateValue as Dayjs;
        setEventType?.(DAY_WEEK_TYPE.DAY);
        setSelectedDate?.(picked.format("YYYY-MM-DD"));
        startDateVal = toISTStart(picked.toDate());
        endDateVal = toISTEnd(picked.toDate());
      }
    }

    const appliedFaculty = isDesktop
      ? filterState?.facultySearch || null
      : selectedFaculty || null;

    setFilterState((prev: any) => ({
      ...prev,
      ...(startDateVal && { startDate: startDateVal }),
      ...(endDateVal && { endDate: endDateVal }),
      subject:
        selectedSubject && selectedSubject !== "ALL"
          ? { label: selectedSubject, value: selectedSubject }
          : null,
      facultySearch: appliedFaculty || "",
      clearFacultySearchInput: false,
    }));
    setSearchedFaculty(appliedFaculty?.employeeId || null);

    if (appliedFaculty) {
      localStorage.setItem("scheduledFilter", JSON.stringify(appliedFaculty));
    } else {
      localStorage.removeItem("scheduledFilter");
    }
    if (!appliedFaculty) {
      setSelectedFaculty(null);
      setInput("");
      setSearchName(null);
      setSuggestions([]);
    }

    onClose?.();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={initialValues}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <FilterHeader onReset={handleReset} onClose={onClose} />

      {/* Faculty Search - Mobile Only */}
      {!isDesktop && (
        <div
          style={{
            width: "100%",
            marginTop: "16px",
          }}
        >
          <AutoSearchInput
            input={input}
            setInput={handleFacultyInputChange}
            suggestions={suggestions}
            setSuggestions={setSuggestions}
            isLoader={isLoader}
            setIsLoader={setIsLoader}
            searchData={searchDataHandler}
            searchName={searchName}
            setSearchName={setSearchName}
            placeholder="Search by faculty email"
            nextHandler={nextHandler}
            hasMore={hasMore}
            mode="faculty_search"
            onClickHandler={onClickHandler}
          />
        </div>
      )}
      <FilterFormBody
        key={resetKey}
        selectedSubject={selectedSubject}
        setSelectedSubject={setSelectedSubject}
        dateValue={dateValue}
        setDateValue={setDateValue}
        subjects={subjects}
      />
      <FilterFooter onCancel={onClose} />
    </Form>
  );
});

FiltersFormScreen.displayName = "FiltersFormScreen";

export default FiltersFormScreen;
